/**
 * Flighting Code parser
 *
 * South African radio flighting codes encode the spot duration and language
 * directly in the code (e.g. SHMA_30_248_E → 30s English; KFC/045/1793/Z → Zulu).
 * The flighting code is the source of truth that broadcasters use to traffic
 * the spot, so when present it overrides whatever language/duration was
 * extracted from the rest of the chase list.
 */

/**
 * Normalise a flighting code / spot name so all separators are underscores
 * AND auto-insert underscores at every alpha↔numeric transition AND after
 * the first 3-digit duration block.
 *
 * Three-step pipeline:
 *
 *   1. Separator conversion. Any of `-`, `/`, `\`, `–` (en-dash), `—` (em-dash)
 *      or whitespace becomes `_`. Studios paste codes with all sorts of
 *      separator conventions; this collapses them to the canonical underscore.
 *
 *   2. Transition auto-insert. An underscore is inserted at every position
 *      where the previous character is a letter and the next is a digit, or
 *      vice versa. Lets the user type `SHMA30248E` and have the field land on
 *      `SHMA_30_248_E` automatically.
 *
 *   3. 3-digit duration auto-insert. SA flighting codes always use the second
 *      segment as the spot duration, and we standardised on 3 digits (e.g.
 *      `030`, `045`, `120`). When the first digit run hits a 4th consecutive
 *      digit, we force an underscore before that 4th digit so the user can
 *      keep typing the campaign id without manually pressing `_`. Only the
 *      FIRST digit run is auto-split — subsequent runs (e.g. `1793` campaign
 *      ids) are left intact.
 *
 * Examples:
 *   SHMA-30-248-E       →   SHMA_30_248_E
 *   SHMA 30 248 E       →   SHMA_30_248_E
 *   SHMA—30—248—E       →   SHMA_30_248_E    (en/em dashes from Word/Excel)
 *   SHMA30248E          →   SHMA_30_248_E    (transition auto-insert)
 *   SHMA0302E           →   SHMA_030_2_E     (3-digit duration auto-insert)
 *   KFC/045/1793/Z      →   KFC_045_1793_Z
 *   KFC045_1793Z        →   KFC_045_1793_Z   (user-typed separator preserved)
 *   KFC0451793Z         →   KFC_045_1793_Z   (3-digit auto-split on first run)
 *   shma_30_e           →   SHMA_30_E        (all letters uppercased)
 *
 * Used at every entry point (chase list parser, Add Audio form, manual edits)
 * so the matching pipeline (`flightingCode === trackName.toLowerCase()`) can
 * find tracks regardless of which format the studio used in the chase list
 * vs the filename.
 *
 * Collapses runs of `_`, trims leading/trailing `_` (unless `partial`), and
 * uppercases the result. Broadcasters always traffic codes in caps, and
 * matching downstream lowercases on both sides so case doesn't break
 * anything either way.
 *
 * Pass `{ partial: true }` from controlled-input onChange handlers so the
 * trailing-underscore trim doesn't eat the separator the user just typed
 * (otherwise typing `_`, `/`, `-`, etc. as the last char looks like nothing
 * happened — the char is converted to `_` and then stripped). Batch / save
 * paths should keep the default so persisted codes stay canonical.
 */
export interface NormaliseFlightingCodeOptions {
  /** Skip the leading/trailing underscore trim. Use from live-typing
   *  handlers so separators typed at the end of the field aren't silently
   *  erased mid-edit. */
  partial?: boolean;
}

export function normaliseFlightingCode(
  input: string | null | undefined,
  options: NormaliseFlightingCodeOptions = {},
): string {
  if (!input) return '';

  // Short-circuit "not applicable" sentinels BEFORE separator conversion.
  // A chase list cell of "N/A" (or "n.a.", "N-A", etc.) means the row has
  // no flighting code — without this early exit the `/` gets converted to
  // `_` producing "N_A", which parseFlightingCode then reads as "A" =
  // Afrikaans (single trailing letter language rule). Collapse to "TBA" so
  // it flows through the same code path as any other missing code.
  // Only matches when the whole cleaned string is a lone "N" separator "A"
  // pattern — a legitimate code like "NA_30_248_E" is left alone.
  // Skipped in `partial` mode so live typing isn't hijacked mid-edit.
  const cleaned = String(input).trim();
  if (!options.partial && /^N[\s._/\\-]*A$/i.test(cleaned)) return 'TBA';

  // Step 1 — convert separator characters to underscores.
  const withSeparators = cleaned.replace(/[-/\\–—\s]+/g, '_');

  // Step 2 — walk the string and insert an underscore at every alpha↔numeric
  // transition. We compare each char's class against the LAST output char's
  // class (not the previous input char) so a separator the user typed earlier
  // is what gates the transition — i.e. an existing `_` blocks the auto-insert,
  // keeping the result idempotent and respecting the user's manual separators.
  //
  // While walking we ALSO apply the 3-digit duration auto-insert: track the
  // first digit run and, when it would extend to a 4th consecutive digit,
  // emit `_` before the 4th. `firstDigitSegmentDone` flips as soon as we
  // leave the first digit segment (either via the auto-insert or via a
  // natural transition / separator) so subsequent digit runs aren't split.
  let out = '';
  let firstDigitSegmentDone = false;
  for (let i = 0; i < withSeparators.length; i++) {
    const curr = withSeparators[i];
    const prev = out.length > 0 ? out[out.length - 1] : '';
    const isCurrAlpha = /[A-Za-z]/.test(curr);
    const isCurrDigit = /\d/.test(curr);
    const isPrevAlpha = /[A-Za-z]/.test(prev);
    const isPrevDigit = /\d/.test(prev);
    if (prev !== '' && prev !== '_' && (
      (isCurrAlpha && isPrevDigit) || (isCurrDigit && isPrevAlpha)
    )) {
      out += '_';
    }
    // 3-digit duration rule: about to append a 4th digit to the first run.
    if (
      isCurrDigit &&
      !firstDigitSegmentDone &&
      /\d{3}$/.test(out)
    ) {
      out += '_';
      firstDigitSegmentDone = true;
    }
    out += curr;
    // Mark the first digit segment as done the moment we land any non-digit
    // after a digit (separator or letter), so a later 3+ digit run won't
    // be auto-split.
    if (!firstDigitSegmentDone && !isCurrDigit && isPrevDigit) {
      firstDigitSegmentDone = true;
    }
  }

  // Step 3 — collapse double underscores from paste / typing artefacts.
  // Step 4 — leading/trailing trim. Skipped in `partial` mode so the live
  //   editor doesn't eat the separator the user is currently typing.
  // Step 5 — uppercase to canonical caps form.
  let result = out.replace(/_+/g, '_');
  if (!options.partial) {
    result = result.replace(/^_+|_+$/g, '');
  }
  return result.toUpperCase();
}

/**
 * Walk an array of codes, normalise each, and return:
 *   - the normalised array
 *   - the count of entries that actually changed
 *
 * Use for batch operations (chase list upload, multi-file drop, batch-fill)
 * where the UI needs to tell the user "X entries were normalised".
 */
export function normaliseFlightingCodeBatch(codes: (string | null | undefined)[]): {
  values: string[];
  changedCount: number;
} {
  let changedCount = 0;
  const values = codes.map((c) => {
    const before = c == null ? '' : String(c);
    const after = normaliseFlightingCode(c);
    if (after !== before) changedCount++;
    return after;
  });
  return { values, changedCount };
}

/**
 * Single-letter language suffix codes.
 *
 * The 11 first entries map to existing options in LANGUAGE_OPTIONS. The
 * less common languages fall back to "Other" since the LANGUAGE_OPTIONS
 * dropdown does not list them; their actual name is kept in `displayName`
 * for tooltips.
 */
export const FLIGHTING_LANGUAGE_CODES: Record<
  string,
  { value: string; label: string; displayName: string }
> = {
  E: { value: 'english',   label: 'English',   displayName: 'English' },
  A: { value: 'afrikaans', label: 'Afrikaans', displayName: 'Afrikaans' },
  Z: { value: 'zulu',      label: 'Zulu',      displayName: 'Zulu' },
  X: { value: 'xhosa',     label: 'Xhosa',     displayName: 'Xhosa' },
  S: { value: 'sotho',     label: 'Sotho',     displayName: 'Sesotho' },
  D: { value: 'ndebele',   label: 'Ndebele',   displayName: 'Ndebele' },
  T: { value: 'tswana',    label: 'Tswana',    displayName: 'Tswana' },
  V: { value: 'venda',     label: 'Venda',     displayName: 'Venda' },
  W: { value: 'swazi',     label: 'Swazi',     displayName: 'Siswati' },
  O: { value: 'tsonga',    label: 'Tsonga',    displayName: 'Tsonga' },
  N: { value: 'pedi',      label: 'Pedi',      displayName: 'Sepedi' },
  // Less common — no direct LANGUAGE_OPTIONS match, fall back to "Other".
  U: { value: 'other',     label: 'Other',     displayName: '!Xun' },
  H: { value: 'other',     label: 'Other',     displayName: 'Khwe' },
  I: { value: 'other',     label: 'Other',     displayName: 'Oshiwambo' },
  // K is Oshiwambo on the OK Foods codes convention — NBC's Oshiwambo
  // station is branded "NBC KATI" so their codes use K instead of I.
  // Same target language as I.
  K: { value: 'other',     label: 'Other',     displayName: 'Oshiwambo' },
  R: { value: 'other',     label: 'Other',     displayName: 'Rukavango' },
  M: { value: 'other',     label: 'Other',     displayName: 'Damara Nama' },
  J: { value: 'other',     label: 'Other',     displayName: 'Otjiherero' },
  G: { value: 'other',     label: 'Other',     displayName: 'German' },
  Q: { value: 'other',     label: 'Other',     displayName: 'Pitori' },
};

/**
 * Detect a Live Read flighting code. Live reads are on-air spots read by
 * the DJ from a script rather than played from an audio file — chase lists
 * flag them with an `LR`, `L/R`, or `/L` suffix on the code (e.g.
 * `SHMA_30_248_LR`, `SHMA_30_248_L_R`, `SHMA_30_248_L`). Live-read rows
 * carry no audio to link, so auto-match skips them and delivery counts
 * them as ready without a linked track.
 *
 * Accepts either raw or normalised codes: normalises internally so the
 * trailing `LR` / `L_R` / `L` segment is detected regardless of the
 * original separator style.
 *
 * Returns false for `null` / empty / `TBA` / `TBC`.
 */
export function isLiveReadCode(input: string | undefined | null): boolean {
  if (!input) return false;
  const normalised = normaliseFlightingCode(input);
  if (!normalised || normalised === 'TBA' || normalised === 'TBC') return false;
  // Anchor to end so a legit "L" mid-code (not a real thing, but future-proof)
  // wouldn't trigger. Longest match first via alternation.
  return /(?:_LR|_L_R|_L)$/.test(normalised);
}

export interface ParsedFlightingCode {
  /** Duration as a display string ("30\"") or null if not extractable. */
  duration: string | null;
  /** A single language option matching LANGUAGE_OPTIONS, or null. */
  language: { value: string; label: string } | null;
  /** Friendly name for the language (e.g. "!Xun"); useful for tooltips
   *  when the matched LANGUAGE_OPTIONS entry is "Other". */
  languageDisplayName: string | null;
}

const SEPARATOR = /[_/\-\s]+/; // _, /, -, or whitespace

/**
 * Parse a flighting code and extract duration + language.
 *
 * Rules:
 * - Splits on `_`, `/`, `-`, or whitespace.
 * - Duration: first segment that's a 1- or 3-digit number in the typical
 *   spot range (5-180 seconds). Returned as `30"` style display string.
 * - Language: searches segments from the end for a single-letter token
 *   that matches FLIGHTING_LANGUAGE_CODES.
 *
 * Returns nulls for fields that can't be extracted, so the caller can fall
 * back to manual entry. Empty / "TBA" / "TBC" codes return all nulls.
 */
export function parseFlightingCode(input: string | undefined | null): ParsedFlightingCode {
  const empty: ParsedFlightingCode = {
    duration: null,
    language: null,
    languageDisplayName: null,
  };
  if (!input || typeof input !== 'string') return empty;

  const cleaned = input.trim();
  if (!cleaned) return empty;
  const upper = cleaned.toUpperCase();
  if (upper === 'TBA' || upper === 'TBC') return empty;

  const segments = cleaned.split(SEPARATOR).map((s) => s.trim()).filter(Boolean);
  if (segments.length < 2) return empty;

  const result: ParsedFlightingCode = { ...empty };

  // Duration: first short numeric segment in the 5-180 range.
  for (const seg of segments) {
    if (/^\d{1,3}$/.test(seg)) {
      const n = parseInt(seg, 10);
      if (n >= 5 && n <= 180) {
        result.duration = `${n}"`;
        break;
      }
    }
  }

  // Language: walk from the end looking for a single-letter match.
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i];
    if (seg.length === 1) {
      const mapped = FLIGHTING_LANGUAGE_CODES[seg.toUpperCase()];
      if (mapped) {
        result.language = { value: mapped.value, label: mapped.label };
        result.languageDisplayName = mapped.displayName;
        break;
      }
    }
  }

  return result;
}
